import React, { useCallback, useEffect, useState } from 'react';
import { SettingsOutlined } from '@duxton-web/icons';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Typography,
} from '@duxton-web/ui';
import scribe from '@grab-x/scribe-web-sdk';
import { produce } from 'immer';
import _ from 'lodash';
import { useParams } from 'react-router-dom';

import {
  ExperimentAction,
  getAssignByFacetFromDomain,
  isCreateMode,
  isEditMode,
} from '../../constants';
import {
  useExperimentContext,
  useExperimentDispatchContext,
} from '../../contexts/experiment-context';
import { Experiment } from '../../domain';
import { updateExperiment } from '../../store/actions/experiment/action-creators';
import { getDomainFamily } from '@/common/domains';
import { newCreationFlowEvents } from '@/common/Scribe/EventsList';
import CommonSelect from '@/common/selects/CommonSelect';
import { useFetchDomainsMetadata } from '@/hooks';
import Service, { IsAlphaNumericWithUnderscore } from '@/lib/App';
import { useAppNavigate } from '@/utils/navigation';
import { deepMerge } from '@/utils/object';
import { EXPERIMENT_SAMPLE_TEMPLATE_URL } from '@/utils/url';

const MainDetailSection = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;

  return (
    <Card
      styles={{
        body: { padding: 24 },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Typography.Text strong>Main Details</Typography.Text>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              message: 'Please enter your name',
            },
            {
              required: true,
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }
                if (IsAlphaNumericWithUnderscore(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error('No special character allowed'),
                );
              },
            },
          ]}
          style={{ margin: 0 }}
        >
          <Input
            maxLength={100}
            showCount
            disabled={isEditMode(action)}
            placeholder="Add a name for your experiment"
          />
        </Form.Item>
        <Form.Item
          label={
            <span>
              Hypothesis (
              <a
                href={EXPERIMENT_SAMPLE_TEMPLATE_URL}
                target="_blank"
                rel="noreferrer"
              >
                Sample Template
              </a>
              )
            </span>
          }
          name={['metadata', 'description']}
          rules={[
            {
              required: true,
              message: 'Please enter your description',
            },
          ]}
          style={{ margin: 0 }}
        >
          <Input.TextArea
            rows={6}
            maxLength={5000}
            showCount
            placeholder="Google Doc / Jira Ticket / Slack Thread link that describes your experiment design and results"
            style={{ borderRadius: 12 }}
          />
        </Form.Item>
      </div>
    </Card>
  );
};

const _DomainSection = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;

  const form = Form.useFormInstance();

  const domain = Form.useWatch('domain', { form, preserve: true });
  const skipDomain = Form.useWatch('skipDomain', { form, preserve: true });

  const { isLoading, data } = useFetchDomainsMetadata();

  return (
    <Card styles={{ body: { padding: 24 } }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Form.Item
          name="domain"
          label="Domain"
          required
          rules={[
            {
              required: !skipDomain,
              message: 'Please select a domain',
            },
          ]}
          style={{ margin: 0 }}
        >
          <CommonSelect
            placeholder="Select a domain"
            allowClear
            values={
              !isLoading
                ? data.filter((item) => {
                    return item.active;
                  })
                : []
            }
            idKey="name"
            labelKey="title"
            descriptionKey="description"
            disabled={skipDomain || isEditMode(action)}
          />
        </Form.Item>
        <Typography.Text>
          Assign by: {getAssignByFacetFromDomain(domain)?.title || '-'}
        </Typography.Text>
        <Typography.Text type="secondary">
          The randomization facet for the selected domain.
        </Typography.Text>
        <div
          style={{
            borderRadius: 12,
            background: '#f5f5f5',
            padding: '8px 16px',
          }}
        >
          <Form.Item
            name="skipDomain"
            valuePropName="checked"
            style={{ margin: 0 }}
          >
            <Checkbox disabled={isEditMode(action)}>
              I’m not sure which domain I need, I would prefer to skip this step
              and select variables.
            </Checkbox>
          </Form.Item>
        </div>
      </div>
    </Card>
  );
};

const OwnershipSection = () => {
  return (
    <Card styles={{ body: { padding: 24 } }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Typography.Text strong>Ownership</Typography.Text>
        <Form.Item
          label="Product Manager"
          name={['metadata', 'productManager']}
          rules={[
            {
              required: true,
              message: 'Please select a Product Manager',
            },
          ]}
          style={{ marginBottom: 8 }}
        >
          <CommonSelect
            idKey="email"
            values={Service.cache!.getBasicUsers().filter(
              ({ email }) => !!email,
            )}
            placeholder="Select a Product Manager for your experiment"
          />
        </Form.Item>
        <Form.Item
          label="Product Analyst"
          name={['metadata', 'productAnalyst']}
          rules={[
            {
              required: true,
              message: 'Please select a Product Analyst',
            },
          ]}
          style={{ margin: 0 }}
        >
          <CommonSelect
            idKey="email"
            values={Service.cache!.getBasicUsers().filter(
              ({ email }) => !!email,
            )}
            placeholder="Select a Product Analyst for your experiment"
          />
        </Form.Item>
      </div>
    </Card>
  );
};

const ExperimentMetadata = () => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const { navigateTo, goBack } = useAppNavigate();

  const experimentContext = useExperimentContext();
  const { experiment } = experimentContext;
  const experimentDispatch = useExperimentDispatchContext();

  const [open, setOpen] = useState<boolean>(false);
  const [isValid, setIsValid] = useState(true);
  const [formValues, setFormValues] = useState<Experiment>(null);
  const [shouldRedirect, setShouldRedirect] = useState<boolean>(
    [ExperimentAction.CLONE, ExperimentAction.CREATE].includes(action),
  );

  useEffect(() => {
    if (isCreateMode(action)) {
      setIsValid(false);
      setOpen(true);
    }
    setFormValues(experiment);
  }, []);

  useEffect(() => {
    if (action === ExperimentAction.CLONE) {
      const nextExperiment = produce(experiment, (draft) => {
        let experimentName = draft?.name;
        if (experimentName) {
          let experimentNames = experimentName.split('.');
          if (experimentNames.length > 1) {
            experimentNames.splice(0, 1);
            experimentNames[0] = `clone_of_${experimentNames[0]}`;
          }

          draft.name = experimentNames.join('.');
          draft.state = '';
        }
      });

      setOpen(true);
      setIsValid(true);
      setFormValues(nextExperiment);
    }
  }, [experiment?.name]);

  useEffect(() => {
    if (isCreateMode(action)) {
      scribe.sendEvent(newCreationFlowEvents.viewCreateExperimentModal.name, {
        STATE_NAME: newCreationFlowEvents.viewCreateExperimentModal.state,
      });
    }
  }, []);

  const [form] = Form.useForm();
  form.setFieldsValue(formValues);

  const handleCancel = () => {
    if (action === ExperimentAction.CLONE) {
      setOpen(false);

      if (shouldRedirect) {
        goBack();
      }

      return;
    }

    scribe.sendEvent(newCreationFlowEvents.clickCancelExperimentModal.name, {
      name: form.getFieldValue('name'),
      description: form.getFieldValue('metadata/description'),
      domain: form.getFieldValue('domain'),
      productManager: form.getFieldValue('metadata/productManager'),
      productAnalyst: form.getFieldValue('metadata/productAnalyst'),
      cancelType: isCreateMode(action) ? 'create' : 'edit',
      STATE_NAME: newCreationFlowEvents.clickCancelExperimentModal.state,
    });

    if (isCreateMode(action)) {
      if (shouldRedirect) {
        navigateTo(`/${getDomainFamily()}/experiments`);
      } else {
        setOpen(false);
      }
    } else {
      setOpen(false);
    }
  };

  const debouncedHandleFormValuesChange = useCallback(
    _.debounce(
      async (experiment: Experiment, changedValues: any, allFields: any) => {
        const nextAllFields = produce(allFields, (draft) => {
          Object.keys(changedValues).forEach((key) => {
            switch (key) {
              case 'skipDomain': {
                draft.domain = null;
                break;
              }
              default:
                break;
            }
          });
        });

        const updatedExperiment = deepMerge(experiment, nextAllFields);

        setFormValues(updatedExperiment as Experiment);

        form.validateFields({ validateOnly: true }).then(
          (_) => {
            setIsValid(true);
          },
          () => {
            setIsValid(false);
          },
        );
      },
      500,
    ),
    [],
  );

  const handleFormValuesChange = (changedValues: any, allFields: any) => {
    debouncedHandleFormValuesChange(experiment, changedValues, allFields);
  };

  return (
    <>
      <Button
        size="small"
        type="secondary"
        shape="circle"
        onClick={() => {
          scribe.sendEvent(newCreationFlowEvents.clickEditExperiment.name, {
            json_dump: JSON.stringify(experiment),
            STATE_NAME: newCreationFlowEvents.clickEditExperiment.state,
          });

          setOpen(true);
        }}
        icon={<SettingsOutlined />}
      />
      <Modal
        open={open}
        centered
        title={`${isCreateMode(action) ? 'Create' : 'Edit'} Experiment`}
        onOk={() => {
          const { configurations, ...experiment } = form.getFieldsValue(true);
          experimentDispatch(updateExperiment(experiment));

          setOpen(false);
          setShouldRedirect(false);
        }}
        onCancel={handleCancel}
        okText="Save"
        okButtonProps={{
          size: 'small',
          disabled: !isValid,
        }}
        cancelButtonProps={{
          size: 'small',
        }}
        closable={!isCreateMode(action)}
        maskClosable={!isCreateMode(action)}
        width={700}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 208px)',
            overflowY: 'auto',
          },
        }}
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={experiment}
          onValuesChange={handleFormValuesChange}
          preserve
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <MainDetailSection />
            {/* <DomainSection /> */}
            <OwnershipSection />
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ExperimentMetadata;
